import type { DateTime } from "luxon";
import type { Department } from "../contexts/AuthProvider";
import { getShiftDisplayDate } from "./dateUtils";
import { formatPersonFullName } from "./formatters";
import type {
	ActualNurse,
	BaseNurseShift,
	HospitalData,
	Nurse,
	NurseInShift,
	NurseTrainingStatus,
	ScheduleActualShift,
	SchedulePersonField,
	ScheduleShift,
	ShiftPerson,
	WeekData,
} from "./models";
import { toIsoDate } from "./scheduleWeeks";
import { snakeToCamel } from "./utilFunctions";

/** Where a schedule day's shift sits in `hospitalData.weeks`: `weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex]`. */
export type ScheduleSlot = { weekIndex: number; shiftIndex: number; dayIndex: number };

/** A schedule day and the shift type a shift is listed under on it: what the save functions take. */
export type ScheduleDayShift = { date: DateTime; shiftType: string };

/** The schedule day a shift is listed on: its start's staffing date, which the department's day start decides. */
export const scheduleDayOfShift = (
	shift: Pick<NurseInShift, "startTime" | "shiftType">,
	department: Pick<Department, "dayStart" | "scheduled_shift_delta_minutes">,
): ScheduleDayShift => ({
	date: getShiftDisplayDate(shift.startTime, department.dayStart, department.scheduled_shift_delta_minutes).startOf("day"),
	shiftType: shift.shiftType,
});

/**
 * The slot of the `shiftType` shift on schedule day `date`, or `undefined` when that day isn't loaded. Each shift type
 * is looked up in its own row: a row skips the days it has no staffing for, so the same date can sit at a different
 * day index in each row.
 */
export const findScheduleSlot = (weeks: WeekData[], date: DateTime, shiftType: string): ScheduleSlot | undefined => {
	const isoDate = toIsoDate(date);
	for (const [weekIndex, week] of weeks.entries()) {
		const shiftIndex = week.weeklyShifts.findIndex((shift) => shift.type === shiftType);
		const dayIndex = shiftIndex === -1 ? -1 : week.weeklyShifts[shiftIndex].days.findIndex((day) => toIsoDate(day.date) === isoDate);
		if (dayIndex !== -1) return { weekIndex, shiftIndex, dayIndex };
	}
	return undefined;
};

/** The schedule day and shift type at `slot`, the inverse of `findScheduleSlot`, for callers that still hold a slot. */
export const scheduleSlotDay = (weeks: WeekData[], { weekIndex, shiftIndex, dayIndex }: ScheduleSlot): ScheduleDayShift => {
	const shift = weeks[weekIndex].weeklyShifts[shiftIndex];
	return { date: shift.days[dayIndex].date, shiftType: shift.type };
};

// Only the shift rows arrive snake_case; the day's own fields (census_low, census_override, ...) are
// read snake_case everywhere, so a deep key conversion would break them.
const camelCaseKeys = <T extends object>(row: T): T => {
	const converted: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(row)) {
		converted[snakeToCamel(key)] = value;
	}
	// The backend sends the same fields under snake_case names; after renaming they are the typed shape.
	return converted as T;
};

/** `department_data`-shaped weeks as the backend sends them → the shape the schedule UI reads. */
export const camelCaseWeekShifts = (weeks: WeekData[]): WeekData[] =>
	weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses?.map(camelCaseKeys),
				nursesActual: day.nursesActual?.map(camelCaseKeys),
			})),
		})),
	}));

/** Scheduled plus punched shift rows across `weeks`; the size of a schedule load. */
export const countShiftRows = (weeks: WeekData[]): number =>
	weeks
		.flatMap((week) => week.weeklyShifts)
		.flatMap((shift) => shift.days)
		// DEFAULT: days before actuals were recorded carry no `nursesActual` list at all, which means no punches.
		.reduce((total, day) => total + day.nurses.length + (day.nursesActual?.length ?? 0), 0);

/** Fills the display fields the backend leaves to the client (name, role, employment type, job name). */
/** `nurse`'s display fields for a shift of theirs; the job's name only when the shift's job is one of the nurse's. */
const nurseDetailsOf = (
	jobId: string | undefined,
	nurse: ShiftPerson,
	lastNameFirst: boolean,
): Pick<BaseNurseShift, SchedulePersonField> & Pick<BaseNurseShift, "jobName"> => {
	const job = jobId && nurse.jobs?.length ? nurse.jobs.find((nurseJob) => nurseJob.id === jobId) : undefined;
	return { role: nurse.role, name: formatPersonFullName(nurse, lastNameFirst), type: nurse.employmentType, ...(job && { jobName: job.displayName }) };
};

export const applyNurseDetails = (shift: NurseInShift | ActualNurse, nurse: Nurse, lastNameFirst: boolean): void => {
	Object.assign(shift, nurseDetailsOf(shift.jobId, nurse, lastNameFirst));
};

/** A copy of `shift` with `nurse`'s display fields filled, so a cached shift stays untouched. The schedule cache sends shifts without them. */
export const withDetails = <Shift extends ScheduleShift | ScheduleActualShift>(
	shift: Shift,
	nurse: ShiftPerson,
	lastNameFirst: boolean,
): Shift & Pick<BaseNurseShift, SchedulePersonField> => ({ ...shift, ...nurseDetailsOf(shift.jobId, nurse, lastNameFirst) });

/** One nurse's weeks with that nurse's display fields filled; copies, so cached weeks stay untouched. */
export const withNurseDetails = (weeks: WeekData[], nurse: Nurse, lastNameFirst: boolean): WeekData[] =>
	weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((shift) => ({
			...shift,
			days: shift.days.map((day) => ({
				...day,
				nurses: day.nurses.map((row) => withDetails(row, nurse, lastNameFirst)),
				nursesActual: day.nursesActual?.map((row) => withDetails(row, nurse, lastNameFirst)),
			})),
		})),
	}));

/** Every scheduled shift in `weeks`, by its id. */
export const scheduledShiftsByIdOf = (weeks: WeekData[]): Map<string, NurseInShift> =>
	new Map(weeks.flatMap((week) => week.weeklyShifts.flatMap((row) => row.days.flatMap((day) => day.nurses.map((shift) => [shift.id, shift] as const)))));

/** The nurse's shifts overlapping `timeFrameStart`..`timeFrameEnd` across `weeks`, earliest first; shifts
 * pending removal are left out. An open end means no bound on that side. */
export const nurseShiftsInTimeFrame = (
	weeks: WeekData[],
	nurseId: number,
	timeFrameStart: DateTime | undefined,
	timeFrameEnd: DateTime | undefined,
	isActual: boolean,
): NurseInShift[] =>
	weeks
		.flatMap((week) => week.weeklyShifts)
		.flatMap((shift) => shift.days)
		// Days before actuals were recorded carry no `nursesActual` list at all.
		.flatMap((day) => (isActual ? (day.nursesActual ?? []) : day.nurses))
		.filter(
			(n) =>
				n.nurseId === nurseId &&
				!n.isPendingToDelete &&
				(timeFrameStart === undefined || n.endTime > timeFrameStart) &&
				(timeFrameEnd === undefined || n.startTime < timeFrameEnd),
		)
		.sort((n1, n2) => n1.startTime.toMillis() - n2.startTime.toMillis());

/**
 * `weeks` with `nurseId`'s training status set on every shift of theirs on `date`, scheduled and actual, in every shift
 * type's row: a training status belongs to the nurse's whole day. Draft mode marks the shifts for publishing. Copies
 * only what changes.
 */
export const withNurseDayTrainingStatus = (
	weeks: WeekData[],
	date: DateTime,
	nurseId: number,
	trainingStatus: NurseTrainingStatus,
	isDraftMode: boolean,
): WeekData[] => {
	const isoDate = toIsoDate(date);
	const withStatus = <Shift extends BaseNurseShift>(shift: Shift): Shift =>
		shift.nurseId === nurseId ? { ...shift, trainingStatus, ...(isDraftMode && { isPendingToUpdate: true }) } : shift;
	return weeks.map((week) => ({
		...week,
		weeklyShifts: week.weeklyShifts.map((row) => ({
			...row,
			days: row.days.map((day) =>
				toIsoDate(day.date) === isoDate ? { ...day, nurses: day.nurses.map(withStatus), nursesActual: day.nursesActual?.map(withStatus) } : day,
			),
		})),
	}));
};

/** A copy of `prevData` whose day at the slot (and the week and row above it) is a fresh object, safe to change. */
export function copyDayData(prevData: HospitalData, weekIndex: number, shiftNo: number, dayNo: number) {
	const newData = { ...prevData, weeks: [...prevData.weeks] };

	newData.weeks[weekIndex] = {
		...prevData.weeks[weekIndex],
		weeklyShifts: [...prevData.weeks[weekIndex].weeklyShifts],
	};

	newData.weeks[weekIndex].weeklyShifts[shiftNo] = {
		...prevData.weeks[weekIndex].weeklyShifts[shiftNo],
		days: [...prevData.weeks[weekIndex].weeklyShifts[shiftNo].days],
	};

	const days = newData.weeks[weekIndex].weeklyShifts[shiftNo].days;
	const day = { ...days[dayNo] };
	days[dayNo] = day;
	return { day, newData };
}
